<template>
  <div
    :id="id"
    v-click-outside="handleClickOutside"
    class="crypkit-select"
    :class="[
      size,
      { 'validation-error': !!errorMessage },
      { collapsed: collapsed, inline: inline, disabled: disabled },
    ]"
    :data-test-label="label"
  >
    <div class="select-wrapper">
      <Label
        v-if="label !== ''"
        :label="label"
        :required="required"
        :hide-asterisk="hideAsterisk"
        :help="help"
      >
        <template v-if="$slots['label-append']" #append>
          <slot name="label-append" />
        </template>
      </Label>

      <div
        ref="select"
        class="select"
        :class="{
          'bulky-border': bulkyBorder,
        }"
        tabindex="0"
        @keyup.enter="selectHovered"
        @keyup.esc="closeSelect"
        @keydown.up.prevent="handleArrowKey(ArrowKeyDirection.UP)"
        @keydown.down.prevent="handleArrowKey(ArrowKeyDirection.DOWN)"
        @click.prevent.stop="collapsed ? closeSelect() : openSelect()"
      >
        <slot name="icon-before" />
        <transition name="fade" mode="out-in">
          <span
            v-if="$slots['selected-text-prepend']"
            :key="getSlotKeyByChildren($slots['selected-text-prepend']()[0])"
            class="flex items-center pr-2 mr-2 border-r border-outline h-3/5"
          >
            <slot name="selected-text-prepend" />
          </span>
        </transition>
        <input
          v-if="searchable && collapsed"
          ref="search-input"
          class="outline-hidden text-sm pr-2"
          type="text"
          :placeholder="noneSelected ? placeholder : selectedText"
          @input="debounceSearch"
          @click.stop
        />
        <span
          v-else
          class="selected-text"
          :class="{
            placeholder: noneSelected,
            'selected-text-multiple':
              !noneSelected && Array.isArray(modelValue),
          }"
        >
          <template v-if="noneSelected">{{ placeholder }}</template>
          <slot v-else name="selected-text" :selected="modelValue">
            {{ selectedText }}
          </slot>
        </span>

        <div class="icon">
          <span
            v-if="(collapsed || clearAlwaysVisible) && clearable"
            class="icon-cell"
          >
            <span class="clear-select" @click.stop="reset">
              <SvgIcon icon="Close" class="shrink-0" block />
            </span>
          </span>
          <!-- closed + always-visible clear: keep the caret next to the ×
               so the select still reads as openable, not just clearable -->
          <span v-if="!(collapsed && clearable)" class="icon-cell">
            <slot name="icon">
              <SvgIcon icon="ArrowDown" class="caret" />
            </slot>
          </span>
        </div>

        <SelectItems
          v-if="collapsed"
          :id="id ? `${id}-items` : null"
          ref="items"
          v-model:collapsed="collapsed"
          :class="itemsClass"
          :position-to="selectRef"
          :options="filteredOptions"
          :text-key="selectedTextKey"
          :selected-value="modelValue"
          :group-values="groupValues"
          :no-options-text="noOptionsText"
          @load-more="$emit('load-more')"
          @select="selectValue"
        >
          <template v-if="$slots['after-options']" #after-options>
            <slot name="after-options" />
          </template>
          <template v-if="$slots['options-footer']" #options-footer>
            <slot name="options-footer" />
          </template>
          <template #item-text="slotProps">
            <slot name="item-text" :option="slotProps.option" />
          </template>
        </SelectItems>
      </div>
    </div>

    <div v-if="!hideErrors" class="validation-messages">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import './Select.scss'

import {
  computed,
  nextTick,
  onMounted,
  ref,
  toRef,
  useTemplateRef,
  watch,
  type VNode,
} from 'vue'
import { useField } from 'vee-validate'
import { debounce } from 'lodash-es'
import { isEqual } from 'lodash-es'
import { v4 as uuidv4 } from 'uuid'
import hash from 'object-hash'

import SelectItems from '@/components/controls/SelectItems'
import SvgIcon from '@/components/misc/SvgIcon'
import Label from '@/components/misc/Label'

import vClickOutside from '@/components/directives/click-outside'

import fuzzySearch from '@/helpers/fuzzySearch'

import type { PropType } from 'vue'

enum ArrowKeyDirection {
  UP = 'up',
  DOWN = 'down',
}

const props = defineProps({
  id: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: () => uuidv4(),
  },
  modelValue: {
    type: [Object, Array] as PropType<object | object[] | null>,
    default: null,
  },
  size: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  hideAsterisk: {
    type: Boolean,
    default: false,
  },
  help: {
    type: String,
    default: null,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  clearAlwaysVisible: {
    type: Boolean,
    default: false,
  },
  inline: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Array as PropType<object[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  selectedOption: {
    type: [Object, Array] as PropType<object | object[] | null>,
    default: null,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: 'Select',
  },
  customSearchHandler: {
    type: Function,
    default: null,
  },
  hideErrors: {
    type: Boolean,
    default: false,
  },
  noOptionsText: {
    type: String,
    default: 'No results found',
  },
  selectedTextKey: {
    type: String,
    default: 'text',
  },
  groupValues: {
    type: String,
    default: null,
  },
  bulkyBorder: {
    type: Boolean,
    default: false,
  },
  itemsClass: {
    type: String,
    default: null,
  },
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: object | object[] | null): void
  (e: 'load-more'): void
  (e: 'select', value: object | object[]): void // ??
}>()

let debounceSearch = debounce(() => {})

const name = toRef(props, 'name')
const initialValue = toRef(props, 'modelValue')

const { value: inputValue, errorMessage } = useField(name, undefined, {
  initialValue,
  validateOnValueUpdate: false,
})

const search = ref('')
const collapsed = ref(false)
const selectRef = useTemplateRef('select')
const itemsRef = useTemplateRef('items')
const searchInputRef = useTemplateRef('search-input')

const noneSelected = computed((): boolean => {
  return (
    inputValue.value == null ||
    (Array.isArray(inputValue.value) && inputValue.value.length == 0)
  )
})

const selectedText = computed((): string => {
  if (!inputValue.value) {
    return ''
  }

  if (Array.isArray(inputValue.value)) {
    if (inputValue.value.length > 1) {
      return `${inputValue.value.length} options selected`
    } else if (inputValue.value.length == 1) {
      return inputValue.value[0][props.selectedTextKey]
    }
    return ''
  }

  return props.selectedTextKey in inputValue.value
    ? inputValue.value[props.selectedTextKey]
    : ''
})

const filteredOptions = computed((): object[] => {
  const {
    options,
    customSearchHandler,
    searchable,
    groupValues,
    selectedTextKey,
  } = props

  if (Array.isArray(inputValue.value)) {
    // selected items on top when multiple is set
    options.sort((a, b) => {
      const isASelected = isSelected(a)
      const isBSelected = isSelected(b)
      if (isASelected && !isBSelected) {
        return -1
      } else if (!isASelected && isBSelected) {
        return 1
      }
      return 0
    })
  }

  if (customSearchHandler != null) {
    return options
  }

  if (searchable && search.value.length > 0) {
    if (groupValues) {
      const filteredGroups: object[] = []

      for (let i = 0; i < options.length; i++) {
        const option = options[i]
        const groupOptions = fuzzySearch(search.value, option[groupValues])
        if (groupOptions.length) {
          const filteredGroup = { ...option }
          filteredGroup[groupValues] = groupOptions
          filteredGroups.push(filteredGroup)
        }
      }
      return filteredGroups
    }
    return fuzzySearch(search.value, options, { keys: [selectedTextKey] })
  } else {
    // delete old search highlighted results
    options.forEach((option: any) => delete option.highlight)
  }

  return options
})

function getSlotKeyByChildren(node: VNode) {
  return hash(node, {
    replacer: (value) => {
      if (value && typeof value === 'object') {
        if ('children' in value) {
          return value.children
        }
        return null
      }
      return value
    },
  })
}

function selectValue(value: object) {
  const { groupValues, searchable } = props
  if (value && groupValues && Array.isArray(value[groupValues])) {
    // do not select group header
    return
  }

  if (Array.isArray(inputValue.value)) {
    const idx = inputValue.value.findIndex((item) => isEqual(item, value))
    if (idx !== -1) {
      inputValue.value.splice(idx, 1)
    } else {
      inputValue.value.push(value)
    }
    if (searchable) {
      searchInputRef.value?.focus()
    }
  } else {
    inputValue.value = value
    closeSelect()
  }

  emit('update:modelValue', inputValue.value)
}

function selectHovered() {
  if (itemsRef.value && itemsRef.value.hasHovered()) {
    itemsRef.value.selectHovered()
  }
}

function reset() {
  inputValue.value = Array.isArray(inputValue.value) ? [] : null
  emit('update:modelValue', inputValue.value)
  closeSelect()
}

function openSelect() {
  if (props.disabled) {
    return
  }
  collapsed.value = true
}

function closeSelect() {
  collapsed.value = false
  search.value = ''
}

function handleClickOutside(event: Event) {
  // check if was clicked on any select item
  if (
    itemsRef.value &&
    !itemsRef.value.$el.contains(event.target as HTMLElement)
  ) {
    closeSelect()
  }
}

function handleArrowKey(direction: ArrowKeyDirection) {
  if (!collapsed.value) {
    collapsed.value = true
    return
  }

  if (itemsRef.value) {
    if (direction == ArrowKeyDirection.UP) {
      itemsRef.value.hoverPrev()
    } else {
      itemsRef.value.hoverNext()
    }
  }
}

function isSelected(option: object): boolean {
  if (noneSelected.value) {
    return false
  }

  return Array.isArray(inputValue.value)
    ? inputValue.value.findIndex((item) => isEqual(item, option)) !== -1
    : isEqual(inputValue.value, option)
}

onMounted(() => {
  debounceSearch = debounce((e: InputEvent) => {
    if (e.target) {
      const input = e.target as HTMLInputElement
      search.value = input.value
    }
  }, 100)
})

// focus search input when select is opened and searchable
watch(collapsed, (collapsed) => {
  if (props.searchable && collapsed) {
    nextTick(() => {
      searchInputRef.value?.focus()
    })
  }
})

watch(
  () => props.selectedOption,
  (val) => {
    if (val) {
      inputValue.value = val
    }
  },
  {
    immediate: true,
  }
)

watch(search, (value) => {
  if (props.customSearchHandler != null) {
    props.customSearchHandler(value)
  }
  // while typing, anchor the list to the top (best matches); when the
  // search is cleared, follow the selection back to its position
  nextTick(() => {
    const items = itemsRef.value
    if (!items) {
      return
    }
    if (value === '' && items.hasHovered()) {
      items.scrollToItem()
    } else {
      items.clearHovered()
      items.resetScroll()
    }
  })
})

watch(
  () => props.modelValue,
  (value) => {
    if (inputValue.value != value) {
      inputValue.value = value
    }
  }
)

defineExpose({ closeSelect })
</script>
